import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useUser } from "../context/UserContext";

const STATUS_OPTIONS = [
  "nieuw",
  "open",
  "in_behandeling",
  "wacht_op_klant",
  "opgelost",
  "gesloten",
];

const PRIORITEIT_OPTIONS = ["laag", "normaal", "hoog", "kritiek"];
const VISIBILITY_OPTIONS = ["publiek", "intern"];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatRequestTypeLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "Service";
  if (key === "offerte_aanvraag") return "Offerte-aanvraag";
  if (key === "storing") return "Storing";
  if (key === "onderhoud") return "Onderhoud";
  if (key === "inspectie") return "Inspectie";
  if (key === "overig") return "Overig";
  return "Service";
}

function getApiMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.apiError?.message ||
    error?.message ||
    fallback
  );
}

function getRoleNames(user) {
  const set = new Set();
  if (user?.rol) set.add(String(user.rol).trim().toLowerCase());
  if (user?.role) set.add(String(user.role).trim().toLowerCase());
  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      set.add(String(role).trim().toLowerCase());
      continue;
    }
    const name = role?.name || role?.naam;
    if (name) set.add(String(name).trim().toLowerCase());
  }
  return Array.from(set);
}

export default function SupportTicketsAdminPage({ mode = "admin" }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const isTenantMode = String(mode) === "tenant";
  const allowCreate = isTenantMode;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detail, setDetail] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingAssignee, setSavingAssignee] = useState(false);
  const [savingMessage, setSavingMessage] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [creatingWorkorder, setCreatingWorkorder] = useState(false);

  const [filters, setFilters] = useState({
    status: "",
    prioriteit: "",
    assigneeUserId: "",
    q: "",
  });

  const [createForm, setCreateForm] = useState({
    subject: "",
    prioriteit: "normaal",
    requesterName: user?.naam || "",
    requesterEmail: user?.email || "",
    assigneeUserId: "",
    message: "",
    visibility: "publiek",
  });

  const [messageForm, setMessageForm] = useState({
    visibility: "publiek",
    message: "",
  });

  const [editForm, setEditForm] = useState({
    status: "",
    assigneeUserId: "",
  });

  const roleNames = useMemo(() => getRoleNames(user), [user]);
  const permissionKeys = useMemo(
    () =>
      Array.isArray(user?.permissions)
        ? user.permissions.map((item) => String(item || "").trim().toLowerCase())
        : [],
    [user]
  );

  const canEdit = useMemo(() => {
    if (permissionKeys.includes("support.tickets.edit")) return true;
    if (permissionKeys.includes("tenants.settings")) return true;
    return (
      roleNames.includes("platformadmin") ||
      roleNames.includes("tenantadmin") ||
      roleNames.includes("projectleider")
    );
  }, [permissionKeys, roleNames]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get("/users");
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.items)
          ? res.data.items
          : [];
      setUsers(
        list
          .map((item) => ({
            _id: String(item?._id || ""),
            naam: String(item?.naam || item?.name || ""),
            email: String(item?.email || ""),
          }))
          .filter((item) => item._id)
      );
    } catch {
      setUsers([]);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.prioriteit) params.prioriteit = filters.prioriteit;
      if (filters.assigneeUserId) params.assigneeUserId = filters.assigneeUserId;
      if (filters.q) params.q = filters.q;

      const res = await api.get("/support-tickets", { params });
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setTickets(items);

      if (!selectedTicketId && items.length > 0) {
        setSelectedTicketId(String(items[0]._id || ""));
      } else if (
        selectedTicketId &&
        !items.some((item) => String(item?._id || "") === String(selectedTicketId))
      ) {
        setSelectedTicketId(items[0]?._id ? String(items[0]._id) : "");
      }
    } catch (err) {
      setError(getApiMessage(err, "Supporttickets laden mislukt"));
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedTicketId]);

  const loadDetail = useCallback(async () => {
    if (!selectedTicketId) {
      setDetail(null);
      setDetailError("");
      return;
    }
    try {
      setDetailLoading(true);
      setDetailError("");
      const res = await api.get(`/support-tickets/${selectedTicketId}`);
      const payload = {
        ticket: res?.data?.ticket || null,
        messages: Array.isArray(res?.data?.messages) ? res.data.messages : [],
      };
      setDetail(payload);
      setEditForm({
        status: String(payload?.ticket?.status || ""),
        assigneeUserId: String(payload?.ticket?.assigneeUserId || ""),
      });
    } catch (err) {
      setDetailError(getApiMessage(err, "Ticketdetail laden mislukt"));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedTicketId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  async function handleCreateTicket(event) {
    event.preventDefault();
    if (!allowCreate) return;
    try {
      setCreatingTicket(true);
      setError("");
      setSuccess("");
      const payload = {
        subject: createForm.subject,
        prioriteit: createForm.prioriteit,
        requesterName: createForm.requesterName,
        requesterEmail: createForm.requesterEmail,
        assigneeUserId: createForm.assigneeUserId || null,
        message: createForm.message,
        visibility: createForm.visibility,
      };
      const res = await api.post("/support-tickets", payload);
      const createdId = String(res?.data?._id || "");
      setCreateForm((prev) => ({
        ...prev,
        subject: "",
        message: "",
      }));
      setSuccess("Supportticket aangemaakt.");
      await loadTickets();
      if (createdId) setSelectedTicketId(createdId);
    } catch (err) {
      setError(getApiMessage(err, "Supportticket aanmaken mislukt"));
    } finally {
      setCreatingTicket(false);
    }
  }

  async function handleSaveStatus() {
    if (!detail?.ticket?._id) return;
    try {
      setSavingStatus(true);
      setError("");
      setSuccess("");
      await api.patch(`/support-tickets/${detail.ticket._id}/status`, {
        status: editForm.status,
      });
      setSuccess("Status bijgewerkt.");
      await Promise.all([loadTickets(), loadDetail()]);
    } catch (err) {
      setError(getApiMessage(err, "Status wijzigen mislukt"));
    } finally {
      setSavingStatus(false);
    }
  }

  async function handleSaveAssignee() {
    if (!detail?.ticket?._id) return;
    try {
      setSavingAssignee(true);
      setError("");
      setSuccess("");
      await api.patch(`/support-tickets/${detail.ticket._id}/assignee`, {
        assigneeUserId: editForm.assigneeUserId || null,
      });
      setSuccess("Assignee bijgewerkt.");
      await Promise.all([loadTickets(), loadDetail()]);
    } catch (err) {
      setError(getApiMessage(err, "Assignee wijzigen mislukt"));
    } finally {
      setSavingAssignee(false);
    }
  }

  async function handleAddMessage(event) {
    event.preventDefault();
    if (!detail?.ticket?._id) return;
    try {
      setSavingMessage(true);
      setError("");
      setSuccess("");
      await api.post(`/support-tickets/${detail.ticket._id}/messages`, {
        visibility: messageForm.visibility,
        message: messageForm.message,
      });
      setMessageForm((prev) => ({
        ...prev,
        message: "",
      }));
      setSuccess("Reactie toegevoegd.");
      await Promise.all([loadTickets(), loadDetail()]);
    } catch (err) {
      setError(getApiMessage(err, "Reactie toevoegen mislukt"));
    } finally {
      setSavingMessage(false);
    }
  }

  async function handleCreateWorkorderFromTicket() {
    if (!detail?.ticket?._id) return;
    if (detail?.ticket?.linkedWorkorderId) {
      navigate(`/werkbon/${detail.ticket.linkedWorkorderId}`);
      return;
    }

    try {
      setCreatingWorkorder(true);
      setError("");
      setSuccess("");
      const res = await api.post(`/support-tickets/${detail.ticket._id}/create-workorder`, {});
      const workorderId = String(res?.data?.workorder?.id || "");
      const werkbonNummer = String(res?.data?.workorder?.werkbonNummer || "");
      setSuccess(
        werkbonNummer
          ? `Werkbon ${werkbonNummer} is aangemaakt vanuit aanvraag.`
          : "Werkbon is aangemaakt vanuit aanvraag."
      );
      await Promise.all([loadTickets(), loadDetail()]);
      if (workorderId) {
        navigate(`/werkbon/${workorderId}`);
      }
    } catch (err) {
      const existingWorkorderId = String(
        err?.response?.data?.details?.linkedWorkorderId || ""
      );
      if (existingWorkorderId) {
        navigate(`/werkbon/${existingWorkorderId}`);
        return;
      }
      setError(
        getApiMessage(
          err,
          "Werkbon aanmaken vanuit aanvraag mislukt. Maak anders handmatig een werkbon."
        )
      );
    } finally {
      setCreatingWorkorder(false);
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-xs uppercase tracking-wide text-slate-500">Logboek24</p>
        <h1 className="text-2xl font-semibold text-slate-900">Support tickets</h1>
        <p className="text-sm text-slate-600">
          {allowCreate
            ? "Maak supporttickets aan vanuit tenantbeheer en volg de afhandeling."
            : "Centrale inbox voor binnengekomen tickets en afhandeling."}
        </p>
      </section>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {success}
        </div>
      )}

      {allowCreate && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
            Nieuw ticket
          </h2>
          <form onSubmit={handleCreateTicket} className="grid gap-3 md:grid-cols-2">
            <input
              value={createForm.subject}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, subject: event.target.value }))
              }
              placeholder="Onderwerp"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              required
            />
            <select
              value={createForm.prioriteit}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, prioriteit: event.target.value }))
              }
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {PRIORITEIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  Prioriteit: {option}
                </option>
              ))}
            </select>
            <input
              value={createForm.requesterName}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, requesterName: event.target.value }))
              }
              placeholder="Aanvrager naam"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              required
            />
            <input
              type="email"
              value={createForm.requesterEmail}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, requesterEmail: event.target.value }))
              }
              placeholder="Aanvrager e-mail"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              required
            />
            <select
              value={createForm.assigneeUserId}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, assigneeUserId: event.target.value }))
              }
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">Geen assignee</option>
              {users.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.naam || item.email || item._id}
                </option>
              ))}
            </select>
            <select
              value={createForm.visibility}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, visibility: event.target.value }))
              }
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {VISIBILITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  Eerste bericht: {option}
                </option>
              ))}
            </select>
            <textarea
              value={createForm.message}
              onChange={(event) =>
                setCreateForm((prev) => ({ ...prev, message: event.target.value }))
              }
              placeholder="Beschrijving / eerste bericht"
              className="min-h-[96px] rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
              required
            />
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={creatingTicket}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {creatingTicket ? "Aanmaken..." : "Ticket aanmaken"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
          Filters
        </h2>
        <div className="grid gap-3 md:grid-cols-4">
          <select
            value={filters.status}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, status: event.target.value }))
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Alle statussen</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <select
            value={filters.prioriteit}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, prioriteit: event.target.value }))
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Alle prioriteiten</option>
            {PRIORITEIT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <select
            value={filters.assigneeUserId}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, assigneeUserId: event.target.value }))
            }
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Alle assignees</option>
            {users.map((item) => (
              <option key={item._id} value={item._id}>
                {item.naam || item.email || item._id}
              </option>
            ))}
          </select>
          <input
            value={filters.q}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, q: event.target.value }))
            }
            placeholder="Zoek ticketnr, onderwerp..."
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
            Tickets
          </div>
          <div className="max-h-[620px] overflow-y-auto">
            {loading && <p className="px-4 py-3 text-sm text-slate-500">Laden...</p>}
            {!loading && tickets.length === 0 && (
              <p className="px-4 py-3 text-sm text-slate-500">Geen tickets gevonden.</p>
            )}
            {!loading &&
              tickets.map((ticket) => {
                const isActive = String(ticket?._id || "") === String(selectedTicketId || "");
                return (
                  <button
                    key={ticket._id}
                    type="button"
                    onClick={() => setSelectedTicketId(String(ticket._id))}
                    className={`w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 ${
                      isActive ? "bg-slate-50" : ""
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-900">
                      {ticket.ticketNumber} · {ticket.subject || "-"}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {formatRequestTypeLabel(ticket.requestType)} ·{" "}
                      {ticket.status} · {ticket.prioriteit} ·{" "}
                      {ticket.assigneeName || ticket.assigneeEmail || "Geen assignee"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Aangemaakt: {formatDateTime(ticket.createdAt)}
                    </p>
                  </button>
                );
              })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {!selectedTicketId && (
            <p className="text-sm text-slate-500">Selecteer een ticket voor detailweergave.</p>
          )}
          {selectedTicketId && detailLoading && (
            <p className="text-sm text-slate-500">Detail laden...</p>
          )}
          {selectedTicketId && detailError && (
            <p className="text-sm text-rose-600">{detailError}</p>
          )}
          {detail?.ticket && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {detail.ticket.ticketNumber} · {detail.ticket.subject || "-"}
                </h3>
                <p className="text-sm text-slate-600">
                  Aanvrager: {detail.ticket.requesterName} ({detail.ticket.requesterEmail})
                </p>
                <p className="text-sm text-slate-700">
                  Type:{" "}
                  <span className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-semibold">
                    {formatRequestTypeLabel(detail.ticket.requestType)}
                  </span>
                </p>
                {detail.ticket.linkedWorkorderId ? (
                  <p className="text-sm text-emerald-700">
                    Gekoppelde werkbon:{" "}
                    <button
                      type="button"
                      onClick={() => navigate(`/werkbon/${detail.ticket.linkedWorkorderId}`)}
                      className="font-semibold underline"
                    >
                      {detail.ticket.linkedWorkorderNumber || "Open werkbon"}
                    </button>
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">Nog geen werkbon gekoppeld.</p>
                )}
                <p className="text-xs text-slate-500">
                  Aangemaakt: {formatDateTime(detail.ticket.createdAt)} · Laatste wijziging:{" "}
                  {formatDateTime(detail.ticket.updatedAt)}
                </p>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleCreateWorkorderFromTicket}
                  disabled={!canEdit || creatingWorkorder}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                  {creatingWorkorder
                    ? "Werkbon maken..."
                    : detail.ticket.linkedWorkorderId
                      ? "Open gekoppelde werkbon"
                      : detail.ticket.requestType === "offerte_aanvraag"
                        ? "Werkbon maken (optioneel)"
                        : "Maak werkbon van aanvraag"}
                </button>
                {detail.ticket.requestType === "offerte_aanvraag" ? (
                  <button
                    type="button"
                    disabled
                    className="ml-2 rounded-lg border border-slate-300 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-500"
                    title="Wordt in een volgende fase toegevoegd."
                  >
                    Offerte voorbereiden (binnenkort)
                  </button>
                ) : null}
              </div>

              {detail.ticket.requestType === "offerte_aanvraag" ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    Offerte-aanvraag details
                  </p>
                  <div className="mt-2 grid gap-2 text-sm text-amber-900 md:grid-cols-2">
                    <p>
                      Gewenste uitvoerdatum:{" "}
                      {formatDate(detail.ticket.quoteRequestMeta?.desiredExecutionDate)}
                    </p>
                    <p>
                      Budgetindicatie: {detail.ticket.quoteRequestMeta?.budgetIndication || "-"}
                    </p>
                    <p>
                      Contactpersoon: {detail.ticket.quoteRequestMeta?.contactPersonName || "-"}
                    </p>
                    <p>
                      Telefoon: {detail.ticket.quoteRequestMeta?.contactPersonPhone || "-"}
                    </p>
                    <p className="md:col-span-2">
                      E-mail: {detail.ticket.quoteRequestMeta?.contactPersonEmail || "-"}
                    </p>
                    <p className="md:col-span-2">
                      Locatie:{" "}
                      {detail.ticket.quoteRequestContext?.location?.name ||
                        detail.ticket.quoteRequestMeta?.locationId ||
                        "-"}
                    </p>
                    <p>
                      Installatie:{" "}
                      {detail.ticket.quoteRequestContext?.installation?.name ||
                        detail.ticket.quoteRequestMeta?.installationId ||
                        "-"}
                    </p>
                    <p>
                      Asset:{" "}
                      {detail.ticket.quoteRequestContext?.asset?.name ||
                        detail.ticket.quoteRequestMeta?.assetId ||
                        "-"}
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                    Status
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={editForm.status}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, status: event.target.value }))
                      }
                      disabled={!canEdit}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50"
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleSaveStatus}
                      disabled={!canEdit || savingStatus}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm disabled:opacity-60"
                    >
                      Opslaan
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                    Assignee
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={editForm.assigneeUserId}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, assigneeUserId: event.target.value }))
                      }
                      disabled={!canEdit}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50"
                    >
                      <option value="">Geen assignee</option>
                      {users.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.naam || item.email || item._id}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleSaveAssignee}
                      disabled={!canEdit || savingAssignee}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm disabled:opacity-60"
                    >
                      Opslaan
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-sm font-semibold text-slate-700">Tijdlijn</h4>
                <div className="max-h-[280px] space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
                  {detail.messages.length === 0 && (
                    <p className="text-sm text-slate-500">Nog geen berichten.</p>
                  )}
                  {detail.messages.map((item) => (
                    <div key={item._id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                      <div className="mb-1 flex items-center justify-between gap-2 text-xs text-slate-600">
                        <span>
                          {item.authorName || item.authorEmail || "Onbekend"} · {item.visibility}
                        </span>
                        <span>{formatDateTime(item.createdAt)}</span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-slate-800">{item.message}</p>
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={handleAddMessage} className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-700">Reactie toevoegen</h4>
                <div className="flex gap-2">
                  <select
                    value={messageForm.visibility}
                    onChange={(event) =>
                      setMessageForm((prev) => ({
                        ...prev,
                        visibility: event.target.value,
                      }))
                    }
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  >
                    {VISIBILITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={messageForm.message}
                  onChange={(event) =>
                    setMessageForm((prev) => ({
                      ...prev,
                      message: event.target.value,
                    }))
                  }
                  className="min-h-[90px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Typ hier je reactie..."
                  required
                />
                <button
                  type="submit"
                  disabled={savingMessage || !canEdit}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                  {savingMessage ? "Opslaan..." : "Reactie plaatsen"}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
